import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import rawConfig from '../checklist.config.json';
import Header from './components/Header';
import Checklist from './components/Checklist';
import MeetingTicker from './components/MeetingTicker';
import CityStrip from './components/CityStrip';
import { validateConfig } from './lib/config';
import { todayLocal, previousEligibleDays, isWeekend } from './lib/dates';
import { fetchState, postCheck, deleteCheck } from './lib/api';
import type { Check } from './lib/types';

const POLL_MS = 30_000;

export default function App() {
  const config = useMemo(() => validateConfig(rawConfig), []);
  const [today, setToday] = useState(() => todayLocal());
  const [checks, setChecks] = useState<Check[]>([]);
  const [error, setError] = useState<string | null>(null);

  const writeInFlight = useRef(0);

  const fromDate = useMemo(() => {
    // Pull 7 calendar days back as the fetch window — enough for staleness math + headroom.
    return previousEligibleDays(today, 7, false)[6] ?? today;
  }, [today]);

  const refresh = useCallback(async () => {
    if (writeInFlight.current > 0) return;
    try {
      const { checks: next } = await fetchState(fromDate, today);
      setChecks(next);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [fromDate, today]);

  // Initial load (and re-load whenever the active day changes)
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Polling effect — also acts as the day-rollover watchdog so a tab left
  // open and visible across midnight switches to the new day's checklist.
  useEffect(() => {
    let id: ReturnType<typeof setInterval> | undefined;
    const tick = () => {
      const current = todayLocal();
      if (current !== today) {
        // Rollover: bump `today`; the effect chain re-fetches the new window.
        setToday(current);
      } else {
        refresh();
      }
    };
    const start = () => {
      if (!id) id = setInterval(tick, POLL_MS);
    };
    const stop = () => {
      if (id) {
        clearInterval(id);
        id = undefined;
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        // Same rollover-aware logic as the poll: if the day changed while
        // hidden, bump `today` (effect chain re-fetches the new window);
        // otherwise just refresh the current day. Avoids a stale fetch of
        // the previous day's window.
        tick();
        start();
      } else {
        stop();
      }
    };
    if (document.visibilityState === 'visible') start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refresh, today]);

  const handleToggle = useCallback(
    async (itemId: string) => {
      const wasChecked = checks.some((c) => c.itemId === itemId && c.date === today);
      const optimistic: Check[] = wasChecked
        ? checks.filter((c) => !(c.itemId === itemId && c.date === today))
        : [...checks, { itemId, date: today, checkedAt: new Date().toISOString() }];
      setChecks(optimistic);

      writeInFlight.current += 1;
      try {
        if (wasChecked) {
          await deleteCheck(itemId, today);
        } else {
          await postCheck(itemId, today);
        }
        setError(null);
      } catch (e) {
        // Revert on failure
        setChecks(checks);
        setError((e as Error).message);
      } finally {
        writeInFlight.current -= 1;
      }
    },
    [checks, today],
  );

  const totalCount = useMemo(() => {
    const weekend = isWeekend(today);
    return config.items.filter((i) => !(weekend && i.weekdaysOnly)).length;
  }, [config.items, today]);

  const doneCount = useMemo(
    () => checks.filter((c) => c.date === today && config.items.some((i) => i.id === c.itemId)).length,
    [checks, today, config.items],
  );

  return (
    <main className="app">
      <Header today={today} doneCount={doneCount} totalCount={totalCount} />
      {error && <div role="alert" className="toast">{error}</div>}
      <Checklist items={config.items} checks={checks} today={today} onToggle={handleToggle} />
      <MeetingTicker />
      <CityStrip />
    </main>
  );
}
